import React, { useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Eye, EyeOff, RefreshCw, Star, MapPin, ExternalLink, Award, Pause, Play, Search, Plus, Pencil, Trash2, X, Globe, Building2, Rocket, Check, ChevronRight, ChevronLeft, Calendar } from "lucide-react";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

/**
 * Platform Admin → Marketplace.
 * Super-admin shell with TWO tabs:
 *   [Listings] — manage every tenant's marketplace presence
 *   [Areas]    — CRUD platform-level Qatar zones (used by AreaPicker + search)
 *
 * Tenant isolation is preserved end-to-end: every endpoint here is gated by
 * `_require_super_admin`. Areas are a PLATFORM reference table by design
 * (not tenant-scoped) — the same area shows for every tenant in their
 * Branch-edit AreaPicker.
 */
export default function PlatformAdminMarketplace() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const [tab, setTab] = useState("listings");

  return (
    <div className="p-6 space-y-5 bg-stone-50 min-h-full" data-testid="platform-admin-marketplace">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">{isAr ? "إدارة الماركت بليس" : "Marketplace Management"}</h1>
          <p className="text-sm text-stone-500 mt-1">{isAr ? "تحكم بظهور المستأجرين + إدارة مناطق قطر." : "Control tenant presence + manage Qatar zones."}</p>
        </div>
      </div>

      {/* Tab strip */}
      <div className="border-b border-stone-200 flex gap-1 bg-white rounded-t-xl px-2" data-testid="admin-mp-tabs">
        {[
          { id: "listings", icon: Building2, label: isAr ? "الصالونات" : "Listings" },
          { id: "areas",    icon: Globe,     label: isAr ? "المناطق"   : "Areas" },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-mp-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition ${active ? "border-amber-400 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-900"}`}
            >
              <Icon size={15} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "listings" && <ListingsTab isAr={isAr} />}
      {tab === "areas"    && <AreasTab    isAr={isAr} />}
    </div>
  );
}

// ============================================================================
// LISTINGS TAB
// ============================================================================
function ListingsTab({ isAr }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState({});
  const [wizardListing, setWizardListing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/marketplace/listings");
      setListings(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? "تعذر التحميل" : "Failed to load"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = listings.filter(l => {
    if (!filter.trim()) return true;
    const f = filter.toLowerCase();
    return (l.slug?.toLowerCase().includes(f) || l.name?.en?.toLowerCase().includes(f) || l.name?.ar?.includes(f) || l.tenant_id?.toLowerCase().includes(f));
  });

  const setBusyFlag = (id, v) => setBusy(b => ({ ...b, [id]: v }));

  const toggleVisibility = async (l) => {
    setBusyFlag(l.listing_id, true);
    try {
      const r = await api.put(`/platform/marketplace/listings/${l.listing_id}/visibility`, { in_marketplace: !(l.visibility?.in_marketplace) });
      setListings(prev => prev.map(x => x.listing_id === l.listing_id ? r.data : x));
      toast.success(isAr ? "تم التحديث" : "Updated");
    } catch (e) { toast.error(e?.response?.data?.detail || (isAr ? "تعذر التحديث" : "Update failed")); }
    finally { setBusyFlag(l.listing_id, false); }
  };
  const setStatus = async (l, status) => {
    setBusyFlag(l.listing_id, true);
    try {
      const r = await api.put(`/platform/marketplace/listings/${l.listing_id}/status`, { status });
      setListings(prev => prev.map(x => x.listing_id === l.listing_id ? r.data : x));
      toast.success(isAr ? "تم التحديث" : "Updated");
    } catch (e) { toast.error(e?.response?.data?.detail || (isAr ? "تعذر التحديث" : "Update failed")); }
    finally { setBusyFlag(l.listing_id, false); }
  };
  const feature = async (l, days) => {
    setBusyFlag(l.listing_id, true);
    try {
      const r = await api.put(`/platform/marketplace/listings/${l.listing_id}/featured`, { days });
      setListings(prev => prev.map(x => x.listing_id === l.listing_id ? r.data : x));
      toast.success(days > 0 ? (isAr ? `مميّز لـ ${days} يوم` : `Featured for ${days} days`) : (isAr ? "أُلغي التمييز" : "Feature removed"));
    } catch (e) { toast.error(e?.response?.data?.detail || (isAr ? "تعذر التحديث" : "Update failed")); }
    finally { setBusyFlag(l.listing_id, false); }
  };
  const refresh = async (l) => {
    setBusyFlag(l.listing_id, true);
    try {
      const r = await api.post(`/platform/marketplace/listings/${l.listing_id}/refresh`);
      setListings(prev => prev.map(x => x.listing_id === l.listing_id ? r.data : x));
      toast.success(isAr ? "تم تحديث المشروع" : "Projection refreshed");
    } catch (e) { toast.error(e?.response?.data?.detail || (isAr ? "تعذر التحديث" : "Refresh failed")); }
    finally { setBusyFlag(l.listing_id, false); }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute top-2.5 start-3 text-stone-400" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={isAr ? "بحث..." : "Search..."} data-testid="admin-mp-search"
            className="w-full ps-9 pe-3 py-2 bg-stone-50 border border-stone-200 rounded-md text-sm text-stone-900 placeholder:text-stone-400 outline-none focus:border-amber-500" />
        </div>
        <button onClick={load} className="px-3 py-2 rounded-md bg-stone-50 border border-stone-200 text-stone-900 text-sm hover:bg-stone-100 flex items-center gap-2">
          <RefreshCw size={14} /> {isAr ? "تحديث" : "Reload"}
        </button>
      </div>

      {loading && <div className="text-center text-stone-500 py-12">{isAr ? "جاري التحميل..." : "Loading..."}</div>}

      {!loading && (
        <div className="bg-stone-50 rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-start px-4 py-3">{isAr ? "الصالون" : "Venue"}</th>
                  <th className="text-start px-4 py-3">Tenant</th>
                  <th className="text-start px-4 py-3">{isAr ? "الحالة" : "Status"}</th>
                  <th className="text-start px-4 py-3">{isAr ? "في الماركت" : "In Marketplace"}</th>
                  <th className="text-start px-4 py-3">{isAr ? "التقييم" : "Rating"}</th>
                  <th className="text-start px-4 py-3">{isAr ? "الفروع" : "Branches"}</th>
                  <th className="text-start px-4 py-3">{isAr ? "مميّز حتى" : "Featured until"}</th>
                  <th className="text-start px-4 py-3">{isAr ? "إجراءات" : "Actions"}</th>
                </tr>
              </thead>
              <tbody className="text-stone-900">
                {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-stone-400">{isAr ? "لا نتائج" : "No listings"}</td></tr>}
                {filtered.map((l) => {
                  const visible = !!l.visibility?.in_marketplace;
                  const isBusy = !!busy[l.listing_id];
                  const featuredActive = l.featured_until && new Date(l.featured_until) > new Date();
                  const branchesWithGeo = (l.branches || []).filter(b => b.lat && b.lng).length;
                  return (
                    <tr key={l.listing_id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`admin-mp-row-${l.listing_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{l.name?.en || l.name?.ar || l.slug}</div>
                        <a href={`/m/s/${l.slug}`} target="_blank" rel="noreferrer" className="text-[10px] text-stone-500 hover:text-stone-200 flex items-center gap-1">
                          /m/s/{l.slug} <ExternalLink size={9} />
                        </a>
                      </td>
                      <td className="px-4 py-3 text-xs text-stone-500 font-mono">{l.tenant_id?.slice(0, 18)}</td>
                      <td className="px-4 py-3"><StatusPill status={l.status} isAr={isAr} /></td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleVisibility(l)} disabled={isBusy} data-testid={`admin-mp-toggle-${l.listing_id}`}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${visible ? "bg-emerald-50 text-emerald-700 border border-emerald-300" : "bg-stone-500/15 text-stone-500 border border-stone-500/30"} hover:scale-105 transition`}>
                          {visible ? <Eye size={12} /> : <EyeOff size={12} />}
                          {visible ? (isAr ? "ظاهر" : "Visible") : (isAr ? "مخفي" : "Hidden")}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-xs">
                          <Star size={12} className="text-amber-500" fill="currentColor" />
                          {(l.rating?.avg || 0).toFixed(1)}
                          <span className="text-stone-500 ms-1">({l.rating?.count || 0})</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span className="inline-flex items-center gap-1"><MapPin size={11} className="text-stone-400" />{(l.branches || []).length} <span className="text-stone-400">({branchesWithGeo} geo)</span></span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {featuredActive ? <span className="text-amber-600 font-semibold">{new Date(l.featured_until).toLocaleDateString(isAr ? "ar-QA" : "en-GB")}</span> : <span className="text-stone-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button onClick={async () => {
                              // Re-project from raw tenant branches first so the
                              // wizard always reflects the freshest geo / area
                              // info — bypasses the cached listing.branches.
                              try {
                                const r = await api.post(`/platform/marketplace/listings/${l.listing_id}/refresh`);
                                setWizardListing(r.data || l);
                                setListings(prev => prev.map(x => x.listing_id === l.listing_id ? (r.data || x) : x));
                              } catch {
                                setWizardListing(l);
                              }
                            }} disabled={isBusy}
                            title={isAr ? "إطلاق على الماركت بليس" : "Onboard to marketplace"}
                            data-testid={`admin-mp-onboard-${l.listing_id}`}
                            className="px-2.5 py-1.5 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs font-bold flex items-center gap-1.5 transition">
                            <Rocket size={12} />
                            {isAr ? "إطلاق" : "Onboard"}
                          </button>
                          {l.status === "active" ? (
                            <button onClick={() => setStatus(l, "paused")} disabled={isBusy} title={isAr ? "إيقاف مؤقت" : "Pause"} className="p-1.5 rounded hover:bg-stone-100 text-stone-600"><Pause size={13} /></button>
                          ) : (
                            <button onClick={() => setStatus(l, "active")} disabled={isBusy} title={isAr ? "تفعيل" : "Activate"} className="p-1.5 rounded hover:bg-stone-100 text-emerald-600"><Play size={13} /></button>
                          )}
                          <button onClick={() => feature(l, featuredActive ? 0 : 14)} disabled={isBusy} title={featuredActive ? (isAr ? "إلغاء التمييز" : "Unfeature") : (isAr ? "تمييز لـ 14 يوم" : "Feature 14d")}
                            className={`p-1.5 rounded hover:bg-stone-100 ${featuredActive ? "text-amber-600" : "text-stone-600"}`} data-testid={`admin-mp-feature-${l.listing_id}`}>
                            <Award size={13} />
                          </button>
                          <button onClick={() => refresh(l)} disabled={isBusy} title={isAr ? "إعادة المشروع" : "Re-project"} className="p-1.5 rounded hover:bg-stone-100 text-stone-600"><RefreshCw size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {wizardListing && (
        <OnboardWizard
          isAr={isAr}
          listing={wizardListing}
          onClose={() => setWizardListing(null)}
          onDone={(updated) => {
            setListings(prev => prev.map(x => x.listing_id === updated.listing_id ? updated : x));
            setWizardListing(null);
          }}
        />
      )}
    </>
  );
}

function StatusPill({ status, isAr }) {
  const map = {
    active:    { bg: "bg-emerald-500/15", text: "text-emerald-300", border: "border-emerald-500/30", label: { ar: "نشط",    en: "Active" } },
    draft:     { bg: "bg-stone-500/15",   text: "text-stone-600",   border: "border-stone-500/30",   label: { ar: "مسودة",  en: "Draft" } },
    paused:    { bg: "bg-amber-500/15",   text: "text-amber-600",   border: "border-amber-500/30",   label: { ar: "موقوف",  en: "Paused" } },
    suspended: { bg: "bg-rose-500/15",    text: "text-rose-600",    border: "border-rose-500/30",    label: { ar: "معلّق",  en: "Suspended" } },
  };
  const tone = map[status] || map.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border ${tone.bg} ${tone.text} ${tone.border}`}>
      {isAr ? tone.label.ar : tone.label.en}
    </span>
  );
}

// ============================================================================
// AREAS TAB
// ============================================================================
const EMPTY_AREA = {
  area_id: "",
  country_code: "qa",
  municipality: "",
  municipality_name: { en: "", ar: "" },
  name: { en: "", ar: "" },
  lat: "",
  lng: "",
  sort: 999,
  active: true,
};

function AreasTab({ isAr }) {
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null); // null | EMPTY_AREA | existing doc

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/marketplace/areas");
      setAreas(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? "تعذر التحميل" : "Failed to load"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = areas.filter(a => {
    if (!filter.trim()) return true;
    const f = filter.toLowerCase();
    return (a.area_id?.toLowerCase().includes(f) || a.name?.en?.toLowerCase().includes(f) || a.name?.ar?.includes(f) || a.municipality?.toLowerCase().includes(f));
  });

  // Group filtered areas by municipality so the operator can scan visually.
  const grouped = filtered.reduce((acc, a) => {
    const key = a.municipality;
    if (!acc[key]) acc[key] = { municipality: key, municipality_name: a.municipality_name, items: [] };
    acc[key].items.push(a);
    return acc;
  }, {});

  // Distinct municipality list for the modal dropdown. Filter out items where
  // municipality is empty (legacy / un-prefixed docs would otherwise produce
  // `id: undefined` and crash the localeCompare sort).
  const munOptions = Object.values(
    areas
      .filter(a => a && a.municipality)
      .reduce((m, a) => {
        if (!m[a.municipality]) m[a.municipality] = { id: a.municipality, name: a.municipality_name };
        return m;
      }, {})
  ).sort((x, y) => (x.id || "").localeCompare(y.id || ""));

  const onDelete = async (a) => {
    const ok = await confirm({
      title: isAr ? `حذف منطقة "${a.name?.ar || a.area_id}"؟` : `Delete area "${a.name?.en || a.area_id}"?`,
      confirmLabel: isAr ? "حذف" : "Delete",
      cancelLabel: isAr ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/platform/marketplace/areas/${a.area_id}`);
      setAreas(prev => prev.filter(x => x.area_id !== a.area_id));
      toast.success(isAr ? "تم الحذف" : "Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? "تعذر الحذف" : "Delete failed"));
    }
  };

  return (
    <>
      {/* Help banner — guides the operator through the area-management flow. */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="admin-areas-help">
        <div className="w-9 h-9 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">
          <Globe size={18} />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-stone-900 mb-1">
            {isAr ? "كيفية إضافة منطقة جديدة" : "How to add a new area"}
          </h3>
          <ol className="text-xs text-stone-600 space-y-0.5 list-decimal ms-4 leading-relaxed">
            <li>{isAr ? "اضغط زر «منطقة جديدة» الأخضر أعلاه يمين الصفحة." : "Click the green “New area” button at the top right."}</li>
            <li>{isAr ? "أدخل مُعرّفاً فريداً (مثل qa_doha_xyz) — أحرف صغيرة فقط." : "Enter a unique ID (e.g. qa_doha_xyz) — lowercase only."}</li>
            <li>{isAr ? "اكتب اسم المنطقة بالعربية والإنجليزية + اختر البلدية." : "Type the name in Arabic & English + pick the municipality."}</li>
            <li>{isAr ? "أدخل lat/lng من خرائط جوجل (انقر على المكان → انسخ الإحداثيات)." : "Enter lat/lng from Google Maps (right-click the spot → copy coordinates)."}</li>
            <li>{isAr ? "اضغط حفظ — ستظهر فوراً للمستأجرين في قائمة المناطق عند إعداد فروعهم." : "Hit Save — it appears immediately in every tenant's area picker."}</li>
          </ol>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute top-2.5 start-3 text-stone-400" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={isAr ? "بحث عن منطقة..." : "Search area..."} data-testid="admin-areas-search"
            className="w-full ps-9 pe-3 py-2 bg-stone-50 border border-stone-200 rounded-md text-sm text-stone-900 placeholder:text-stone-400 outline-none focus:border-amber-500" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="px-3 py-2 rounded-md bg-stone-50 border border-stone-200 text-stone-900 text-sm hover:bg-stone-100 flex items-center gap-2">
            <RefreshCw size={14} /> {isAr ? "تحديث" : "Reload"}
          </button>
          <button onClick={() => setEditing({ ...EMPTY_AREA })} data-testid="admin-areas-new-btn"
            className="px-4 py-2.5 rounded-md bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 shadow-md flex items-center gap-2 transition">
            <Plus size={16} /> {isAr ? "منطقة جديدة" : "New area"}
          </button>
        </div>
      </div>

      <div className="text-xs text-stone-400">
        {isAr ? `${areas.length} منطقة في ${Object.keys(grouped).length} بلدية` : `${areas.length} areas across ${Object.keys(grouped).length} municipalities`}
      </div>

      {loading && <div className="text-center text-stone-500 py-12">{isAr ? "جاري التحميل..." : "Loading..."}</div>}

      {!loading && (
        <div className="space-y-4">
          {Object.values(grouped)
            .filter(g => g && g.municipality)
            .sort((a, b) => (a.municipality || "").localeCompare(b.municipality || ""))
            .map((g) => (
            <div key={g.municipality} className="bg-stone-50 rounded-xl border border-stone-200 overflow-hidden" data-testid={`admin-areas-muni-${g.municipality}`}>
              <div className="px-4 py-2.5 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-stone-900">{isAr ? g.municipality_name?.ar : g.municipality_name?.en} <span className="text-xs text-stone-500 ms-2 font-mono">{g.municipality}</span></div>
                </div>
                <div className="text-xs text-stone-400">{g.items.length} {isAr ? "منطقة" : "areas"}</div>
              </div>
              <div className="divide-y divide-stone-100">
                {g.items.map((a) => (
                  <div key={a.area_id} className="px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-stone-50" data-testid={`admin-area-row-${a.area_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-stone-900 truncate">{isAr ? a.name?.ar : a.name?.en} <span className="text-[10px] text-stone-500 ms-2 font-mono">{a.area_id}</span></div>
                      <div className="text-[11px] text-stone-500 mt-0.5 flex flex-wrap items-center gap-3">
                        <span>{isAr ? a.name?.en : a.name?.ar}</span>
                        {a.lat != null && a.lng != null && (
                          <a href={`https://maps.google.com/?q=${a.lat},${a.lng}`} target="_blank" rel="noreferrer" className="hover:text-stone-200 flex items-center gap-1 font-mono">
                            <MapPin size={10} /> {Number(a.lat).toFixed(3)}, {Number(a.lng).toFixed(3)}
                          </a>
                        )}
                        <span>sort: {a.sort}</span>
                        {!a.active && <span className="text-rose-600">{isAr ? "غير نشط" : "Inactive"}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditing(a)} title={isAr ? "تعديل" : "Edit"} className="p-1.5 rounded hover:bg-stone-100 text-stone-600" data-testid={`admin-area-edit-${a.area_id}`}><Pencil size={13} /></button>
                      <button onClick={() => onDelete(a)} title={isAr ? "حذف" : "Delete"} className="p-1.5 rounded hover:bg-rose-500/20 text-rose-600" data-testid={`admin-area-delete-${a.area_id}`}><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {Object.keys(grouped).length === 0 && (
            <div className="text-center py-12 text-stone-500 bg-stone-50 rounded-xl">{isAr ? "لا مناطق" : "No areas"}</div>
          )}
        </div>
      )}

      {editing && (
        <AreaModal
          isAr={isAr}
          initial={editing}
          isNew={!editing.area_id}
          munOptions={munOptions}
          onCancel={() => setEditing(null)}
          onSaved={(saved) => {
            setAreas(prev => {
              const exists = prev.find(x => x.area_id === saved.area_id);
              return exists ? prev.map(x => x.area_id === saved.area_id ? saved : x) : [...prev, saved];
            });
            setEditing(null);
          }}
        />
      )}
      <ConfirmDialogSlot />
    </>
  );
}

// ============================================================================
// AREA MODAL — create / edit
// ============================================================================
function AreaModal({ isAr, initial, isNew, munOptions, onCancel, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [munMode, setMunMode] = useState(
    // For new areas default to "pick" — most zones live inside an existing
    // municipality. Only switch to "new" when creating a brand-new one.
    !initial.area_id || (initial.municipality && munOptions.find(m => m.id === initial.municipality)) ? "pick" : "new"
  );

  const set = (path, v) => {
    setForm(f => {
      const next = { ...f };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = v;
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        lat: form.lat === "" || form.lat === null ? null : Number(form.lat),
        lng: form.lng === "" || form.lng === null ? null : Number(form.lng),
        sort: Number(form.sort) || 999,
      };
      let r;
      if (isNew) {
        r = await api.post("/platform/marketplace/areas", payload);
      } else {
        r = await api.put(`/platform/marketplace/areas/${form.area_id}`, payload);
      }
      toast.success(isNew ? (isAr ? "تم إنشاء المنطقة" : "Area created") : (isAr ? "تم التحديث" : "Updated"));
      onSaved(r.data);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || (isAr ? "تعذر الحفظ" : "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="admin-area-modal" onMouseDown={(e) => onBackdropMouseDown(e, onCancel)}>
      <form onSubmit={onSubmit} className="bg-white border border-stone-200 rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onMouseDown={onModalPanelMouseDown}>
        <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-base font-bold text-stone-900">{isNew ? (isAr ? "إنشاء منطقة" : "New area") : (isAr ? "تعديل منطقة" : "Edit area")}</h2>
          <button type="button" onClick={onCancel} className="p-1.5 rounded hover:bg-stone-100 text-stone-600"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {/* area_id (only when creating) */}
          {isNew && (
            <Field label={isAr ? "المعرّف (area_id)" : "ID (area_id)"} hint={isAr ? "أحرف صغيرة وأرقام و _ فقط" : "lowercase, digits, underscore"} required>
              <input value={form.area_id} onChange={(e) => set("area_id", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                data-testid="admin-area-id" required placeholder="qa_doha_my_area"
                className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded text-stone-900 font-mono outline-none focus:border-amber-500" />
            </Field>
          )}

          {/* names */}
          <div className="grid grid-cols-2 gap-3">
            <Field label={isAr ? "الاسم بالإنجليزي" : "Name (EN)"} required>
              <input value={form.name?.en || ""} onChange={(e) => set("name.en", e.target.value)} data-testid="admin-area-name-en" required
                className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded text-stone-900 outline-none focus:border-amber-500" />
            </Field>
            <Field label={isAr ? "الاسم بالعربي" : "Name (AR)"} required>
              <input value={form.name?.ar || ""} onChange={(e) => set("name.ar", e.target.value)} data-testid="admin-area-name-ar" required dir="rtl"
                className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded text-stone-900 outline-none focus:border-amber-500" />
            </Field>
          </div>

          {/* municipality */}
          <Field label={isAr ? "البلدية" : "Municipality"} required>
            <div className="flex items-center gap-2 mb-2 text-xs">
              <label className="flex items-center gap-1 text-stone-600 cursor-pointer">
                <input type="radio" checked={munMode === "pick"} onChange={() => setMunMode("pick")} /> {isAr ? "اختيار" : "Pick existing"}
              </label>
              <label className="flex items-center gap-1 text-stone-600 cursor-pointer">
                <input type="radio" checked={munMode === "new"} onChange={() => setMunMode("new")} /> {isAr ? "بلدية جديدة" : "New municipality"}
              </label>
            </div>
            {munMode === "pick" ? (
              <select value={form.municipality} required onChange={(e) => {
                const v = e.target.value;
                const m = munOptions.find(x => x.id === v);
                set("municipality", v);
                if (m) set("municipality_name", m.name);
              }} data-testid="admin-area-municipality-pick"
                className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded text-stone-900 outline-none focus:border-amber-500">
                <option value="">{isAr ? "اختر بلدية..." : "Choose..."}</option>
                {munOptions.map(m => <option key={m.id} value={m.id}>{`${isAr ? m.name?.ar : m.name?.en} (${m.id})`}</option>)}
              </select>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <input value={form.municipality} onChange={(e) => set("municipality", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                  placeholder="al_xyz" required data-testid="admin-area-municipality-id"
                  className="px-3 py-2 bg-stone-50 border border-stone-200 rounded text-stone-900 font-mono text-xs outline-none focus:border-amber-500" />
                <input value={form.municipality_name?.en || ""} onChange={(e) => set("municipality_name.en", e.target.value)} placeholder="EN" required
                  className="px-3 py-2 bg-stone-50 border border-stone-200 rounded text-stone-900 text-xs outline-none focus:border-amber-500" />
                <input value={form.municipality_name?.ar || ""} onChange={(e) => set("municipality_name.ar", e.target.value)} placeholder="AR" required dir="rtl"
                  className="px-3 py-2 bg-stone-50 border border-stone-200 rounded text-stone-900 text-xs outline-none focus:border-amber-500" />
              </div>
            )}
          </Field>

          {/* coords */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Lat" hint="25.286">
              <input type="number" step="0.000001" value={form.lat ?? ""} onChange={(e) => set("lat", e.target.value)} data-testid="admin-area-lat"
                className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded text-stone-900 font-mono outline-none focus:border-amber-500" />
            </Field>
            <Field label="Lng" hint="51.531">
              <input type="number" step="0.000001" value={form.lng ?? ""} onChange={(e) => set("lng", e.target.value)} data-testid="admin-area-lng"
                className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded text-stone-900 font-mono outline-none focus:border-amber-500" />
            </Field>
            <Field label={isAr ? "الترتيب" : "Sort"}>
              <input type="number" value={form.sort ?? 999} onChange={(e) => set("sort", e.target.value)} data-testid="admin-area-sort"
                className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded text-stone-900 font-mono outline-none focus:border-amber-500" />
            </Field>
          </div>

          <Field label={isAr ? "الحالة" : "Status"}>
            <label className="flex items-center gap-2 text-stone-600 cursor-pointer">
              <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} data-testid="admin-area-active" />
              {isAr ? "نشطة (ستظهر في قائمة المناطق للمستأجرين)" : "Active (visible in tenant area pickers)"}
            </label>
          </Field>
        </div>

        <div className="px-5 py-3 border-t border-stone-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded bg-stone-50 border border-stone-200 text-stone-900 text-sm hover:bg-stone-100">{isAr ? "إلغاء" : "Cancel"}</button>
          <button type="submit" disabled={saving} data-testid="admin-area-save"
            className="px-4 py-2 rounded bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 disabled:opacity-50">
            {saving ? (isAr ? "..." : "...") : (isNew ? (isAr ? "إنشاء" : "Create") : (isAr ? "حفظ" : "Save"))}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-stone-600 mb-1.5">
        {label} {required && <span className="text-rose-600">*</span>}
        {hint && <span className="text-stone-500 font-normal ms-2 font-mono">{hint}</span>}
      </label>
      {children}
    </div>
  );
}


// ============================================================================
// ONBOARD WIZARD — 3 steps: Period → Branches → Confirm
// ============================================================================
const PERIOD_PRESETS = [
  { days: 0,   label_ar: "بدون انتهاء (تجريبي)", label_en: "No expiry (trial)" },
  { days: 14,  label_ar: "أسبوعان",              label_en: "2 weeks" },
  { days: 30,  label_ar: "شهر واحد",             label_en: "1 month" },
  { days: 90,  label_ar: "3 أشهر",               label_en: "3 months" },
  { days: 180, label_ar: "6 أشهر",               label_en: "6 months" },
  { days: 365, label_ar: "سنة كاملة",            label_en: "1 year" },
];

function OnboardWizard({ isAr, listing, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [periodDays, setPeriodDays] = useState(30);
  const [featured, setFeatured] = useState(false);
  const [selectedBranchIds, setSelectedBranchIds] = useState(() => {
    // Pre-select branches that already had `in_marketplace=true`. Fall back
    // to ALL branches that have valid geo (lat+lng) — the most common case.
    const branches = listing.branches || [];
    const alreadyEnabled = branches.filter(b => b.in_marketplace).map(b => b.branch_id);
    if (alreadyEnabled.length > 0) return alreadyEnabled;
    return branches.filter(b => b.lat && b.lng).map(b => b.branch_id);
  });
  const [saving, setSaving] = useState(false);

  const branches = listing.branches || [];
  const venueName = listing.name?.[isAr ? "ar" : "en"] || listing.name?.en || listing.slug;

  const toggleBranch = (bid) => {
    setSelectedBranchIds((prev) => prev.includes(bid) ? prev.filter(x => x !== bid) : [...prev, bid]);
  };

  const onConfirm = async () => {
    if (selectedBranchIds.length === 0) {
      toast.error(isAr ? "اختر فرعاً واحداً على الأقل" : "Pick at least one branch");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/platform/marketplace/listings/${listing.listing_id}/onboard`, {
        period_days: periodDays,
        branch_ids: selectedBranchIds,
        featured,
      });
      toast.success(isAr ? "تم إطلاق الصالون على الماركت بليس 🎉" : "Tenant launched on marketplace 🎉");
      onDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? "تعذر الإطلاق" : "Onboard failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="admin-onboard-wizard" onMouseDown={(e) => onBackdropMouseDown(e, onClose)}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl" onMouseDown={onModalPanelMouseDown}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-200 flex items-start justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Rocket size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-stone-900">
                {isAr ? "إطلاق صالون على الماركت بليس" : "Launch tenant to Marketplace"}
              </h2>
              <p className="text-xs text-stone-500 mt-0.5">{venueName} <span className="text-stone-400 font-mono ms-2">{listing.slug}</span></p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded hover:bg-stone-100 text-stone-500"><X size={18} /></button>
        </div>

        {/* Step indicator */}
        <div className="px-6 py-4 border-b border-stone-200 bg-stone-50">
          <div className="flex items-center gap-3 text-xs">
            {[
              { i: 1, label_ar: "الفترة",  label_en: "Period" },
              { i: 2, label_ar: "الفروع",  label_en: "Branches" },
              { i: 3, label_ar: "التأكيد", label_en: "Confirm" },
            ].map((s, idx, arr) => (
              <React.Fragment key={s.i}>
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold ${
                    step === s.i ? "bg-emerald-600 text-white" :
                    step > s.i ? "bg-emerald-100 text-emerald-700" :
                    "bg-stone-200 text-stone-500"
                  }`}>
                    {step > s.i ? <Check size={13} /> : s.i}
                  </div>
                  <span className={`${step >= s.i ? "text-stone-900 font-semibold" : "text-stone-400"}`}>
                    {isAr ? s.label_ar : s.label_en}
                  </span>
                </div>
                {idx < arr.length - 1 && <div className={`flex-1 h-px ${step > s.i ? "bg-emerald-300" : "bg-stone-300"}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step body */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-period">
              <div>
                <h3 className="font-bold text-stone-900 mb-1">{isAr ? "اختر فترة العرض" : "Choose listing duration"}</h3>
                <p className="text-sm text-stone-500">{isAr ? "كم من الوقت سيظهر الصالون في الماركت بليس؟ يمكنك التجديد لاحقاً." : "How long should this venue appear in the public marketplace? You can renew later."}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {PERIOD_PRESETS.map((p) => (
                  <button
                    key={p.days}
                    onClick={() => setPeriodDays(p.days)}
                    data-testid={`wizard-period-${p.days}`}
                    className={`text-start p-4 rounded-xl border-2 transition ${
                      periodDays === p.days
                        ? "border-emerald-600 bg-emerald-50"
                        : "border-stone-200 hover:border-stone-400 bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-stone-900">{isAr ? p.label_ar : p.label_en}</span>
                      {periodDays === p.days && <Check size={16} className="text-emerald-600" />}
                    </div>
                    <div className="text-xs text-stone-500 flex items-center gap-1">
                      <Calendar size={11} />
                      {p.days === 0 ? (isAr ? "بدون تاريخ انتهاء" : "No expiry date") : `${p.days} ${isAr ? "يوم" : "days"}`}
                    </div>
                  </button>
                ))}
              </div>
              {periodDays > 0 && (
                <label className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer">
                  <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} data-testid="wizard-featured" />
                  <Award size={15} className="text-amber-600" />
                  <span className="text-sm text-stone-900">
                    {isAr ? `جعله "مميّز" خلال هذه الفترة (${periodDays} يوم)` : `Mark as Featured during this period (${periodDays} days)`}
                  </span>
                </label>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-branches">
              <div>
                <h3 className="font-bold text-stone-900 mb-1">{isAr ? "اختر الفروع التي ستظهر" : "Pick branches to publish"}</h3>
                <p className="text-sm text-stone-500">
                  {isAr
                    ? `للمستأجر ${branches.length} فرع. اختر الفروع التي ستظهر للعملاء على الماركت بليس.`
                    : `Tenant has ${branches.length} branch(es). Pick the ones to publish to the marketplace.`}
                </p>
              </div>
              {branches.length === 0 && (
                <div className="p-6 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-700">
                  {isAr ? "هذا المستأجر ليس له فروع. اطلب منه إضافة فرع أولاً." : "This tenant has no branches. Ask them to add at least one branch first."}
                </div>
              )}
              <div className="space-y-2">
                {branches.map((b) => {
                  const selected = selectedBranchIds.includes(b.branch_id);
                  const hasGeo = b.lat && b.lng;
                  const hasArea = !!b.area_id;
                  return (
                    <button
                      key={b.branch_id}
                      onClick={() => toggleBranch(b.branch_id)}
                      data-testid={`wizard-branch-${b.branch_id}`}
                      className={`w-full text-start p-4 rounded-xl border-2 transition flex items-center gap-4 ${
                        selected ? "border-emerald-600 bg-emerald-50" : "border-stone-200 hover:border-stone-400 bg-white"
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${selected ? "border-emerald-600 bg-emerald-600" : "border-stone-300 bg-white"}`}>
                        {selected && <Check size={14} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-stone-900 truncate">{b.name?.[isAr ? "ar" : "en"] || b.name?.en || b.branch_id}</div>
                        <div className="text-xs text-stone-500 mt-1 flex flex-wrap gap-2">
                          {hasGeo ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700"><MapPin size={11} />{b.lat.toFixed(3)}, {b.lng.toFixed(3)}</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-rose-600"><MapPin size={11} />{isAr ? "بدون إحداثيات" : "No coordinates"}</span>
                          )}
                          {hasArea ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700"><Globe size={11} />{b.area_label || b.area_id}</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-600"><Globe size={11} />{isAr ? "بدون منطقة" : "No area set"}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedBranchIds.length === 0 && branches.length > 0 && (
                <div className="text-xs text-rose-600">{isAr ? "اختر فرعاً واحداً على الأقل للمتابعة." : "Select at least one branch to continue."}</div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="wizard-step-confirm">
              <h3 className="font-bold text-stone-900 mb-1">{isAr ? "راجع الإعدادات" : "Review settings"}</h3>
              <div className="rounded-xl border border-stone-200 divide-y divide-stone-100">
                <Row label={isAr ? "الصالون" : "Venue"} value={venueName} />
                <Row label={isAr ? "الفترة" : "Period"} value={periodDays === 0 ? (isAr ? "بدون انتهاء" : "No expiry") : `${periodDays} ${isAr ? "يوم" : "days"}`} />
                <Row label={isAr ? "مميّز" : "Featured"} value={featured ? (isAr ? "نعم" : "Yes") : (isAr ? "لا" : "No")} />
                <Row label={isAr ? "الفروع المختارة" : "Selected branches"} value={`${selectedBranchIds.length} / ${branches.length}`} />
                <Row label={isAr ? "الظهور" : "Visibility"} value={isAr ? "ظاهر للعملاء فوراً" : "Visible to customers immediately"} valueClass="text-emerald-700 font-semibold" />
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-stone-700 flex items-start gap-3">
                <Check size={18} className="text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  {isAr
                    ? "بعد الضغط على «إطلاق»، سيظهر هذا الصالون فوراً في الماركت بليس العام عند /m وسيستقبل الحجوزات تلقائياً."
                    : "After clicking Launch, this venue will appear immediately on the public marketplace at /m and start accepting bookings."}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-stone-200 flex items-center justify-between sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={step === 1 ? onClose : () => setStep(step - 1)}
            className="px-4 py-2 rounded-lg text-stone-600 hover:bg-stone-100 text-sm font-semibold flex items-center gap-2"
          >
            {step === 1 ? (
              <>{isAr ? "إلغاء" : "Cancel"}</>
            ) : (
              <>{isAr ? <><ChevronRight size={14} /> السابق</> : <><ChevronLeft size={14} /> Back</>}</>
            )}
          </button>
          {step < 3 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={step === 2 && selectedBranchIds.length === 0}
              data-testid="wizard-next-btn"
              className="px-6 py-2.5 rounded-lg bg-stone-900 text-white text-sm font-bold hover:bg-stone-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isAr ? "التالي" : "Next"}
              {isAr ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <button
              type="button"
              onClick={onConfirm}
              disabled={saving}
              data-testid="wizard-launch-btn"
              className="px-6 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2 shadow-md"
            >
              <Rocket size={15} />
              {saving ? (isAr ? "جاري الإطلاق..." : "Launching...") : (isAr ? "إطلاق الآن" : "Launch now")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, valueClass = "" }) {
  return (
    <div className="px-4 py-3 flex items-center justify-between">
      <span className="text-sm text-stone-500">{label}</span>
      <span className={`text-sm text-stone-900 font-semibold ${valueClass}`}>{value}</span>
    </div>
  );
}
